import { createMachine, immediate, invoke, reduce, state, transition } from 'robot3'
import { DateTime } from 'luxon'
import router from 'next/router'
import cloneDeep from 'lodash/cloneDeep'
import { getMarketDetailsByName } from '../../lib/MarketDetails'
import * as Sentry from '@sentry/nextjs'
import { d } from 'robot3'
import { AvailabilityWindowRanges } from './AppointmentScreen'

// ! logging to help debug state machine issues
if (process.env.NODE_ENV === 'development') {
  d._onEnter = function (machine, to, state, prevState, event) {
    console.log(`Enter state ${to}`)
    console.groupCollapsed(`Details:`)
    console.log(`Machine`, machine)
    console.log(`Current state`, state)
    console.log(`Previous state`, state)

    if (typeof event === 'string') {
      console.log(`Event ${event}`)
    } else if (typeof event === 'object') {
      console.log(`Event`, event)
    }

    console.groupEnd()
  }
}

const routeTo = async (url: string) => {
  return router.push(url)
}

export interface MachineContextService {
  name?: string
  customerPrice?: number
  inEstimate?: boolean
  items?: {
    customerPrice?: number
    productSelection?: {
      name?: string
    }
    type?: string
    units?: number
  }[]
}

export interface machineContext {
  history: string[]
  queryParams: string
  job?: {
    locationType?: string
    status?: string
    id?: string
    jobNumber?: string
    serviceLocation?: string
    vehicleInfo?: {
      make?: string
      model?: string
      year?: string
      vin?: string
      licensePlate?: string
      licensePlateState?: string
    }
    signedDocument?: any
    /**
     * Server-derived signals for the appointment tracker (Job.appointmentTracker). Booleans only:
     * drawing the tracker used to mean fetching the job's part orders, which carry vendor, part
     * number, store and cost.
     */
    appointmentTracker?: {
      partsOrdered?: boolean | null
      partsPickedUp?: boolean | null
      technicianActiveInField?: boolean | null
    } | null
    partsInfo?: {
      partsOrderNumber?: string
      partsLocation?: string
      partsOrdered?: boolean
      cancellationInitiated?: boolean
      cancellationTimestamp?: any | null
      partsOrderedTimestamp?: any | null
      partsNotes?: string
      reasonRescheduled?: string
      partsOrderingIssue?: string
      callForPartsTicketNumber?: string
    }
    vehicleId?: string
    vehicle?: {
      make?: string
      model?: string
      year?: string
      vin?: string
      licensePlate?: string
      licensePlateState?: string
    }
    appointment?: {
      id?: string
      endDate?: any | null
      startDate?: any | null
      timeZone?: string | null
      timeWindow?: { startTimeWindow?: any | null; endTimeWindow?: any | null } | null
    } | null
    contact?: {
      id?: string
      email?: string
      fullName?: string
      phoneNumber?: string
      lead?: {
        serviceUrgency?: string
      }
    }
    services?: MachineContextService[]
    priceInfo?: {
      subTotal?: number
      partsTotal?: number
      laborTotal?: number
      discounts?: {
        type?: string
        reason?: string
        amount?: number
        total?: number
        label?: string
        packageDiscountCopy?: {
          id?: string
          name?: string
          description?: string
          active?: boolean
          stackable?: boolean
          createdAt?: string
          updatedAt?: string
          valid_from?: string
          valid_to?: string
          rules?: {
            discount?: {
              type?: string
              value?: number
              label?: string
            }
            when?: {
              field?: string
              operator?: string
              value?: string
              values?: string[]
            }[]
          }[]
        }
      }[]
      promoCodes?: {
        code?: string
        coupon?: {
          name?: string
          discount?: {
            type?: string
            reason?: string
            amount?: number
            total?: number
          }
        }
      }[]
      discountTotal: number
      totalTax: number
      amountDue?: number
    }
    market?: string
    createdBy?: string
    partsLeadTimeInDays?: number
    symptomDiagnosisCategory?: string
    contactPaidInvoices?: number | null
  }
  window?: AvailabilityWindowRanges<DateTime>
  isPriority?: boolean
  formattedWindowTimes?: AvailabilityWindowRanges<DateTime>
  apptMmDdYy?: string
  apptDayOfWeek?: string
  apptTime?: string
  daysToAppt?: number
  sameDayApptsAvail?: number
  nextDayApptsAvail?: number
  marketTz?: string
  serviceAddress?: {
    'places-autocomplete': string
  }
  isReschedule?: boolean
  vinLp?: {
    VIN?: string
    licensePlate?: string
    licensePlateState?: {
      name?: string
      label?: string
    }
  }
  error: {
    message: string
  }
  updatedEmail?: string
  showNoAppointmentsDialog: boolean
  showGenericErrorDialog: boolean
  availabilitySnapshotIDs: string[]
  marketPhone: string
  formattedMarketPhone: string
  refetchJob: boolean
  outOfServiceError: boolean
  refetchAppointments: boolean
  showEstimateDialog: boolean
  selfScheduleVehicleInfoUpdateIdentifier?: string
  estimateBreakdownDialog: boolean
  diagnosisDialog: boolean
  contactDialog: boolean
  vehicleMistmatch?: boolean
  vinLookupFailed?: boolean
}

const context = (): machineContext => {
  const appointmentData = JSON.parse(sessionStorage.getItem('eventDate'))
  if (appointmentData?.window) {
    appointmentData.window = {
      startRange: {
        startTime: DateTime.fromISO(appointmentData.window.startRange.startTime),
        endTime: DateTime.fromISO(appointmentData.window.startRange.endTime),
      },
      arrivalRange: {
        startTime: DateTime.fromISO(appointmentData.window.arrivalRange.startTime),
        endTime: DateTime.fromISO(appointmentData.window.arrivalRange.endTime),
      },
    }
  }
  if (appointmentData?.formattedWindowTimes) {
    appointmentData.formattedWindowTimes = {
      startRange: {
        startTime: DateTime.fromISO(appointmentData.window.startRange.startTime),
        endTime: DateTime.fromISO(appointmentData.window.startRange.endTime),
      },
      arrivalRange: {
        startTime: DateTime.fromISO(appointmentData.window.arrivalRange.startTime),
        endTime: DateTime.fromISO(appointmentData.window.arrivalRange.endTime),
      },
    }
  }
  return {
    history: [],
    queryParams: '',
    error: { message: '' },
    showNoAppointmentsDialog: false,
    showGenericErrorDialog: false,
    availabilitySnapshotIDs: [],
    marketPhone: getMarketDetailsByName('default').phoneNumber,
    formattedMarketPhone: getMarketDetailsByName('default').phoneNumberTxt,
    refetchJob: false,
    outOfServiceError: false,
    refetchAppointments: false,
    showEstimateDialog: false,
    serviceAddress: {
      'places-autocomplete': sessionStorage.getItem('serviceAddress'),
    },
    estimateBreakdownDialog: false,
    diagnosisDialog: false,
    contactDialog: false,
    isReschedule: false,
    ...appointmentData,
  }
}

// generic transition function for saving a single field to the context, allows for single level depth updates i.e. event["data"], but not event["data"]["field"]
// use to replace this oft-seen pattern:
// transition(
//   "event",
//   "nextState",
//   reduce((ctx: machineContext, event) => ({ ...ctx, field: event["data"] }))
// );
const saveSinglePropertyTransition = ({
  eventName,
  nextState,
  prop,
}: {
  eventName: string
  nextState: string
  prop: string
}) => {
  return transition(
    eventName,
    nextState,
    reduce((ctx: machineContext, event) => ({ ...cloneDeep(ctx), [prop]: event[prop] }))
  )
}
const changeStateWithoutNavigation = (nextState: string) => {
  return transition(nextState, nextState)
}

// states
export const MACHINE_ENTRY = 'machineEntry'
export const BASE_ADDRESS_SCREEN = 'baseAddressScreen'
export const BASE_APPOINTMENT_SCREEN = 'baseAppointmentScreen'
export const BASE_ESTIMATE_SCREEN = 'baseEstimateScreen'
export const BASE_FINAL_DETAILS_SCREEN = 'baseFinalDetailsScreen'
export const BASE_BOOKING_COMPLETE_SCREEN = 'baseBookingCompleteScreen'
export const ERROR_STATE = 'errorState'
export const IDLE = 'idle'

const ROUTE_TO_ADDRESS_SCREEN = 'routeToAddressScreen'
const ROUTE_TO_APPOINTMENT_SCREEN = 'routeToAppointmentScreen'
const ROUTE_TO_FINAL_DETAILS_SCREEN = 'routeToFinalDetailsScreen'
const ROUTE_TO_SUCCESS_SCREEN = 'routeToSuccessScreen'
const SEND_RESET_STATE = 'resetState'

// events
export const VIEW_APPOINTMENTS_CLICKED = 'viewAppointmentsClicked'
export const QUERY_PARAMS_SAVED = 'queryParamsSaved'
export const OOS_OK_PRESSED = 'oosOkPressed'
export const EST_BREAKDOWN_DIALOG = 'estBreakdownDialog'
export const EST_CONTACT_DIALOG = 'estContactDialog'
export const DIAGNOSIS_DIALOG = 'diagnosisDialog'
export const GEN_ERR_OK_PRESSED = 'genErrOkPressed'
export const NO_APPT_OK_PRESSED = 'noApptOkPress'
export const NO_APPTS_FOR_SERVICE_ADDRESS = 'noAppointmentsForServiceAddress'
export const APPOINTMENT_COUNT_CAPTURED = 'captureApptCount'
export const WINDOW_SAVED = 'saveWindow'
export const FINAL_DETAILS_SUBMITTED = 'submitDetails'
export const DONE = 'done'
export const ERROR = 'error'
export const RESET = 'reset'
export const REFRESH = 'refresh'
export const EST_DIALOG_CLOSED = 'estDialogClosed'
export const EST_DIALOG_OPENED = 'estDialogOpened'
export const SCHEDULE_SERVICE_CLICKED = 'scheduleServiceClicked'
export const GENERIC_SUBMISSION_ERROR = 'genericSubmissionError'
export const JOB_REFRESH_REQUESTED = 'jobRefreshRequested'
export const JOB_REFRESH_COMPLETED = 'jobRefreshCompleted'
// -- landing pages events
export const ESTIMATE_PAGE_REQUESTED = 'estimatePageRequested'
export const ADDRESS_PAGE_REQUESTED = 'addressPageRequested'
export const FINAL_DETAILS_PAGE_REQUESTED = 'finalDetailsPageRequested'
export const BOOKING_COMPLETE_PAGE_REQUESTED = 'bookingCompletePageRequested'
export const APPOINTMENT_PAGE_REQUESTED = 'appointmentPageRequested'
export const RESCHEDULE_APPOINTMENT_CLICKED = 'rescheduleAppointmentClicked'

export const machine = createMachine(
  {
    // ** relatively linear state machine, errors grouped at bottom **
    // loaded customer info that we have, awaiting customer input plus click to view appts
    machineEntry: state(
      saveSinglePropertyTransition({
        eventName: QUERY_PARAMS_SAVED,
        nextState: MACHINE_ENTRY,
        prop: 'queryParams',
      }),
      transition(
        ESTIMATE_PAGE_REQUESTED,
        BASE_ESTIMATE_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
        }))
      ),
      transition(
        ADDRESS_PAGE_REQUESTED,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
        }))
      ),
      transition(
        FINAL_DETAILS_PAGE_REQUESTED,
        BASE_FINAL_DETAILS_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
        }))
      ),
      transition(
        BOOKING_COMPLETE_PAGE_REQUESTED,
        BASE_BOOKING_COMPLETE_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
        }))
      ),
      transition(
        APPOINTMENT_PAGE_REQUESTED,
        BASE_APPOINTMENT_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
          serviceAddress: { 'places-autocomplete': job?.serviceLocation ?? '' },
        }))
      ),
      transition(
        JOB_REFRESH_REQUESTED,
        MACHINE_ENTRY,
        reduce((ctx: machineContext, event: { refetchJob?: boolean }) => ({
          ...cloneDeep(ctx),
          refetchJob: event.refetchJob,
        }))
      ),
      transition(
        JOB_REFRESH_COMPLETED,
        BASE_ESTIMATE_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
          refetchJob: false,
        }))
      )
    ),

    baseEstimateScreen: state(
      saveSinglePropertyTransition({
        eventName: QUERY_PARAMS_SAVED,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'queryParams',
      }),
      saveSinglePropertyTransition({
        eventName: EST_DIALOG_CLOSED,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'showEstimateDialog',
      }),
      saveSinglePropertyTransition({
        eventName: EST_DIALOG_OPENED,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'showEstimateDialog',
      }),
      saveSinglePropertyTransition({
        eventName: EST_BREAKDOWN_DIALOG,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'estimateBreakdownDialog',
      }),
      saveSinglePropertyTransition({
        eventName: EST_CONTACT_DIALOG,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'contactDialog',
      }),
      saveSinglePropertyTransition({
        eventName: DIAGNOSIS_DIALOG,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'diagnosisDialog',
      }),
      transition(SCHEDULE_SERVICE_CLICKED, ROUTE_TO_ADDRESS_SCREEN),
      transition(
        GEN_ERR_OK_PRESSED,
        BASE_ESTIMATE_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, showGenericErrorDialog: false }))
      ),
      saveSinglePropertyTransition({
        eventName: JOB_REFRESH_REQUESTED,
        nextState: BASE_ESTIMATE_SCREEN,
        prop: 'refetchJob',
      }),
      transition(
        JOB_REFRESH_COMPLETED,
        BASE_ESTIMATE_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
          refetchJob: false,
        }))
      ),
      // Reschedule from estimate: prepare context for AddressScreen
      // (we now redirect to `/book-now`, then AddressScreen handles the flow
      // and routes to the appointment screen via VIEW_APPOINTMENTS_CLICKED).
      transition(
        RESCHEDULE_APPOINTMENT_CLICKED,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({
          ...ctx,
          isReschedule: true,
          serviceAddress: { 'places-autocomplete': ctx.job?.serviceLocation ?? '' },
        }))
      ),
      changeStateWithoutNavigation(BASE_ADDRESS_SCREEN)
    ),

    baseAddressScreen: state(
      saveSinglePropertyTransition({
        eventName: QUERY_PARAMS_SAVED,
        nextState: BASE_ADDRESS_SCREEN,
        prop: 'queryParams',
      }),
      saveSinglePropertyTransition({
        eventName: VIEW_APPOINTMENTS_CLICKED,
        nextState: ROUTE_TO_APPOINTMENT_SCREEN,
        prop: 'serviceAddress',
      }),
      transition(
        OOS_OK_PRESSED,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, outOfServiceError: false }))
      ),
      transition(
        REFRESH,
        SEND_RESET_STATE,
        reduce((ctx: machineContext) => ({ ...ctx, refetchJob: true, showGenericErrorDialog: true }))
      ),
      saveSinglePropertyTransition({ eventName: ERROR, nextState: ERROR_STATE, prop: ERROR }),
      transition(RESET, SEND_RESET_STATE),
      saveSinglePropertyTransition({
        eventName: NO_APPTS_FOR_SERVICE_ADDRESS,
        nextState: ROUTE_TO_ADDRESS_SCREEN,
        prop: 'showNoAppointmentsDialog',
      }),
      transition(
        NO_APPT_OK_PRESSED,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, showNoAppointmentsDialog: false }))
      ),
      transition(
        GEN_ERR_OK_PRESSED,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, showGenericErrorDialog: false }))
      ),
      changeStateWithoutNavigation(BASE_ESTIMATE_SCREEN),
      changeStateWithoutNavigation(BASE_APPOINTMENT_SCREEN)
    ),

    refetchAppointments: invoke(
      (ctx: machineContext) => routeTo(`/book-now/select-appointment?${ctx.queryParams}`),
      transition(DONE, BASE_APPOINTMENT_SCREEN),
      transition(ERROR, ERROR_STATE)
    ),

    // appt selection screen
    baseAppointmentScreen: state(
      transition(
        'refetchedAppointments',
        'baseAppointmentScreen',
        reduce((ctx: machineContext) => ({
          ...ctx,
          refetchAppointments: false,
        }))
      ),
      saveSinglePropertyTransition({
        eventName: EST_CONTACT_DIALOG,
        nextState: BASE_APPOINTMENT_SCREEN,
        prop: 'contactDialog',
      }),
      transition(
        WINDOW_SAVED,
        ROUTE_TO_FINAL_DETAILS_SCREEN,
        reduce((ctx: machineContext, ev: any) => ({
          ...ctx,
          isPriority: ev.isPriority,
          window: ev.data,
          formattedWindowTimes: ev.formattedWindowTimes,
          apptMmDdYy: ev.apptMmDdYy,
          apptDayOfWeek: ev.apptDayOfWeek,
          apptTime: ev.apptTime,
          sameDayApptsAvail: ev.sameDayApptsAvail,
          nextDayApptsAvail: ev.nextDayApptsAvail,
          marketTz: ev.marketTz,
          daysToAppt: ev.daysToAppt,
        }))
      ),
      changeStateWithoutNavigation(BASE_ADDRESS_SCREEN),
      changeStateWithoutNavigation(BASE_FINAL_DETAILS_SCREEN),
      saveSinglePropertyTransition({
        eventName: 'OutOfServiceAreaError',
        nextState: ROUTE_TO_ADDRESS_SCREEN,
        prop: 'outOfServiceError',
      }),
      saveSinglePropertyTransition({ eventName: ERROR, nextState: ERROR_STATE, prop: ERROR }),
      transition(RESET, SEND_RESET_STATE),
      transition(
        NO_APPTS_FOR_SERVICE_ADDRESS,
        ROUTE_TO_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, showNoAppointmentsDialog: true }))
      ),
      transition(
        GEN_ERR_OK_PRESSED,
        BASE_APPOINTMENT_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, showGenericErrorDialog: false }))
      ),
      saveSinglePropertyTransition({
        eventName: 'saveAvailabilitySnapshotIDs',
        nextState: BASE_APPOINTMENT_SCREEN,
        prop: 'availabilitySnapshotIDs',
      })
    ),

    // final details screen
    baseFinalDetailsScreen: state(
      saveSinglePropertyTransition({
        eventName: EST_DIALOG_CLOSED,
        nextState: BASE_FINAL_DETAILS_SCREEN,
        prop: 'showEstimateDialog',
      }),
      saveSinglePropertyTransition({
        eventName: EST_DIALOG_OPENED,
        nextState: BASE_FINAL_DETAILS_SCREEN,
        prop: 'showEstimateDialog',
      }),
      saveSinglePropertyTransition({
        eventName: EST_BREAKDOWN_DIALOG,
        nextState: BASE_FINAL_DETAILS_SCREEN,
        prop: 'estimateBreakdownDialog',
      }),
      transition(
        FINAL_DETAILS_SUBMITTED,
        ROUTE_TO_SUCCESS_SCREEN,
        reduce((ctx: machineContext, event) => ({
          ...ctx,
          updatedEmail: event?.['updatedEmail'],
          vinLp: event?.['vinLp'],
          selfScheduleVehicleInfoUpdateIdentifier: event?.['selfScheduleVehicleInfoUpdateIdentifier'],
          vehicleMistmatch: event?.['vehicleMistmatch'],
          vinLookupFailed: event?.['vinLookupFailed'],
        }))
      ),
      transition(
        'noAppointmentDuringSubmission',
        ROUTE_TO_APPOINTMENT_SCREEN,
        reduce((ctx: machineContext) => ({ ...ctx, refetchAppointments: true }))
      ),
      transition(
        GENERIC_SUBMISSION_ERROR,
        ROUTE_TO_APPOINTMENT_SCREEN,
        reduce((ctx: machineContext, { error }) => {
          Sentry.captureException(error)
          return {
            ...ctx,
            refetchAppointments: true,
            error,
            showGenericErrorDialog: true,
          }
        })
      ),
      changeStateWithoutNavigation(BASE_APPOINTMENT_SCREEN),
      transition(RESET, SEND_RESET_STATE),
      saveSinglePropertyTransition({
        eventName: JOB_REFRESH_REQUESTED,
        nextState: BASE_FINAL_DETAILS_SCREEN,
        prop: 'refetchJob',
      }),
      transition(
        JOB_REFRESH_COMPLETED,
        BASE_ESTIMATE_SCREEN,
        reduce((ctx: machineContext, { data: { getJobPublic: job }, marketPhone, formattedMarketPhone }) => ({
          ...ctx,
          job,
          marketPhone,
          formattedMarketPhone,
          refetchJob: false,
        }))
      )
    ),

    // booking complete screen
    baseBookingCompleteScreen: state(
      saveSinglePropertyTransition({
        eventName: EST_BREAKDOWN_DIALOG,
        nextState: BASE_BOOKING_COMPLETE_SCREEN,
        prop: 'estimateBreakdownDialog',
      }),
      saveSinglePropertyTransition({
        eventName: DIAGNOSIS_DIALOG,
        nextState: BASE_BOOKING_COMPLETE_SCREEN,
        prop: 'diagnosisDialog',
      }),
      transition('bookingComplete', 'doneForNow'),
      // The myappointment hub renders the booked appointment from THIS state, not from
      // baseEstimateScreen, so "change my appointment" has to be able to start a reschedule
      // here too. Without it the event is dropped, `isReschedule` stays false and the final
      // details screen submits createSelfScheduleRequest instead of createSelfReschedule.
      transition(
        RESCHEDULE_APPOINTMENT_CLICKED,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({
          ...ctx,
          isReschedule: true,
          serviceAddress: { 'places-autocomplete': ctx.job?.serviceLocation ?? '' },
        }))
      ),
      transition(BASE_FINAL_DETAILS_SCREEN, 'base404Screen')
    ),
    doneForNow: state(),

    // routing
    routeToAddressScreen: invoke(
      (ctx: machineContext) => routeTo(`/book-now?${ctx.queryParams}`),
      transition(DONE, BASE_ADDRESS_SCREEN),
      transition(ERROR, ERROR_STATE)
    ),
    routeToAppointmentScreen: invoke(
      (ctx: machineContext) => routeTo(`/book-now/select-appointment?${ctx.queryParams}`),
      transition(DONE, BASE_APPOINTMENT_SCREEN),
      transition(ERROR, ERROR_STATE)
    ),
    routeToFinalDetailsScreen: invoke(
      (ctx: machineContext) => routeTo(`/book-now/final-details?${ctx.queryParams}`),
      transition(DONE, BASE_FINAL_DETAILS_SCREEN),
      transition(ERROR, ERROR_STATE)
    ),
    routeToSuccessScreen: invoke(
      (ctx: machineContext) => routeTo(`/book-now/success?${ctx.queryParams}`),
      transition(DONE, BASE_BOOKING_COMPLETE_SCREEN),
      transition(ERROR, ERROR_STATE)
    ),

    // not found, 404
    base404Screen: invoke(
      (ctx: machineContext) => routeTo(`/404?${ctx.queryParams}`),
      transition(
        DONE,
        ERROR_STATE,
        reduce((ctx: machineContext) => ({
          ...ctx,
          appointment: undefined,
          sameDayApptsAvail: undefined,
          nextDayApptsAvail: undefined,
          vinLp: undefined,
          updatedEmail: undefined,
          vehicleMistmatch: undefined,
          vinLookupFailed: undefined,
        }))
      )
    ),

    // errors
    outOfServiceAreaError: state(immediate(BASE_ADDRESS_SCREEN)),
    // TODO: this should be dynamic to send back to either the address page or the estimate page, depending on self-booking status
    resetState: invoke(
      (ctx: machineContext) => routeTo(`/book-now?${ctx.queryParams}`),
      transition(
        DONE,
        BASE_ADDRESS_SCREEN,
        reduce((ctx: machineContext) => ({
          ...ctx,
          ...context,
          window: undefined,
          sameDayApptsAvail: undefined,
          nextDayApptsAvail: undefined,
          vinLp: undefined,
          updatedEmail: undefined,
          vehicleMistmatch: undefined,
          vinLookupFailed: undefined,
          selfScheduleVehicleInfoUpdateIdentifier: undefined,
        }))
      ),
      transition(ERROR, ERROR_STATE)
    ),
    errorState: state(),
  },
  context
)
